import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, VolumeX, Thermometer, Layers, Ruler } from "lucide-react";
import { Reveal, Eyebrow, MaskedLine } from "../components/Reveal";
import { EnquiryModal } from "../components/EnquiryModal";
import { PRODUCTS, COLORS, GLASS, IMAGES } from "../data/products";

const TABS = [
  { key: "all", label: "All Systems", testid: "product-filter-all" },
  { key: "windows", label: "Windows", testid: "product-filter-windows" },
  { key: "doors", label: "Doors", testid: "product-filter-doors" },
];

const SPEC_ICONS = { uValue: Thermometer, sound: VolumeX, glass: Layers, profile: Ruler };
const SPEC_LABELS = { uValue: "U-Value", sound: "Sound Cut", glass: "Glass", profile: "Profile" };

export default function Products() {
  const [tab, setTab] = useState("all");
  const [enquiry, setEnquiry] = useState(null);

  const filtered = tab === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.categories.includes(tab));

  return (
    <div data-testid="products-page">
      <section className="grain-overlay relative flex min-h-[60vh] items-end overflow-hidden" data-testid="products-hero">
        <img src="/images/products-hero.jpg" alt="SOHOM sliding door systems" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 sm:px-10">
          <Eyebrow className="mb-6">uPVC Product Catalogue — Hyderabad</Eyebrow>
          <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Windows &amp; doors,</MaskedLine>
            <MaskedLine delay={0.32} className="text-gold">specified like instruments.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:py-24">
        <div className="flex flex-wrap gap-3" data-testid="product-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={t.testid}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                tab === t.key
                  ? "bg-gold text-obsidian"
                  : "border border-border bg-surface text-slate-400 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="group flex flex-col overflow-hidden rounded-xl border border-sky-400/20 bg-carddark shadow-2xl transition-shadow duration-500 hover:shadow-[0_0_50px_-15px_rgba(0,160,227,0.3)]"
                data-testid={`product-card-${p.id}`}
              >
                <div className="relative h-60 overflow-hidden">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur-md">
                    {p.badge}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-heading text-xl font-bold text-slate-100">{p.name}</h3>
                  <p className="mt-1 text-sm italic text-gold/80">{p.tagline}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{p.description}</p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {Object.entries(p.specs).map(([k, v]) => {
                      const Icon = SPEC_ICONS[k];
                      return (
                        <div key={k} className="rounded-lg border border-border bg-surface/70 px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3 w-3 text-gold" />
                            <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">{SPEC_LABELS[k]}</span>
                          </div>
                          <p className="mt-1 text-xs font-semibold text-slate-200">{v}</p>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setEnquiry(p.name)}
                    data-testid={`enquire-${p.id}`}
                    className="group/btn mt-6 flex items-center justify-center gap-2 rounded-full border border-gold/50 py-3 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
                  >
                    Enquire About This System
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-t border-border bg-surface/40" data-testid="customization-matrix">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
          <Reveal>
            <Eyebrow>Make It Yours</Eyebrow>
            <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              The customization matrix.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-12 lg:grid-cols-2">
            <Reveal>
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Color Options</h3>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  {COLORS.map((c) => (
                    <div key={c.name} className="group" data-testid={`color-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                      <div className="aspect-[4/5] overflow-hidden rounded-lg border border-border transition-colors duration-300 group-hover:border-gold/60">
                        <img src={c.image} alt={c.name} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">{c.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Glass Options</h3>
                <div className="mt-6 flex flex-wrap gap-3">
                  {GLASS.map((g) => (
                    <span key={g} className="rounded-full border border-border bg-carddark px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-gold/50 hover:text-gold" data-testid={`glass-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {enquiry && <EnquiryModal product={enquiry} onClose={() => setEnquiry(null)} />}
    </div>
  );
}
